import type { DataSourceConfig, DataFieldMapping, DataParserConfig } from '@dtwin/shared';
import client from '../api/client';

/** 兼容新旧数据源结构：返回统一 `{ type, config, refreshInterval?, mapping?, parser? }` */
function normalize(ds: any): DataSourceConfig | null {
  if (!ds) return null;
  // 旧结构：{ type, config }
  if (ds.type && ds.config) return ds as DataSourceConfig;
  // 新四段式结构：{ dataType, staticData, apiUrl, ... }
  if (ds.dataType) {
    const cfg: any = {};
    switch (ds.dataType) {
      case 'static': cfg.data = ds.staticData ?? []; break;
      case 'api': cfg.url = ds.apiUrl; cfg.method = ds.method || 'GET'; cfg.headers = ds.headers || {}; cfg.params = ds.params || {}; cfg.body = ds.body; break;
      case 'websocket': cfg.url = ds.wsUrl; break;
      case 'sql': cfg.connectionId = ds.sqlConnectionId; cfg.query = ds.sqlQuery; break;
      case 'mqtt': cfg.brokerUrl = ds.mqttBrokerUrl; cfg.topic = ds.mqttTopic; break;
    }
    return { type: ds.dataType, config: cfg, refreshInterval: ds.refreshInterval, mapping: ds.mapping, parser: ds.parser };
  }
  return null;
}

// ============================================
// 数据解析管线：取值 → 字段映射 → 过滤 → JS 转换
// ============================================

/** JSONPath 简化版取值：支持 a.b.c / a[0].b / a.0.b / list[2] */
function resolvePath(raw: unknown, path?: string): unknown {
  if (!path) return raw;
  const segs = path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean);
  let cur: unknown = raw;
  for (const seg of segs) {
    if (cur == null) return undefined;
    if (Array.isArray(cur)) {
      const idx = Number(seg);
      cur = Number.isInteger(idx) ? cur[idx] : undefined;
    } else if (typeof cur === 'object') {
      cur = (cur as Record<string, unknown>)[seg];
    } else {
      return undefined;
    }
  }
  return cur;
}

/** 字段值格式化：数值 / 字符串 / 百分比 / 千分位 / 日期 */
function formatValue(v: unknown, type?: DataFieldMapping['type'], format?: string): unknown {
  if (v == null || v === '') return v;
  const num = Number(v);
  switch (type) {
    case 'number': {
      const dec = format != null && !Number.isNaN(Number(format)) ? Number(format) : 2;
      return Number.isNaN(num) ? v : Number(num.toFixed(dec));
    }
    case 'percent': {
      const dec = format != null && !Number.isNaN(Number(format)) ? Number(format) : 1;
      return Number.isNaN(num) ? v : `${(num * 100).toFixed(dec)}%`;
    }
    case 'thousand': {
      const dec = format != null && !Number.isNaN(Number(format)) ? Number(format) : 0;
      if (Number.isNaN(num)) return v;
      return num.toLocaleString('zh-CN', { minimumFractionDigits: dec, maximumFractionDigits: dec });
    }
    case 'date': {
      const d = new Date(v as string);
      if (Number.isNaN(d.getTime())) return v;
      const fmt = format || 'YYYY-MM-DD HH:mm:ss';
      const pad = (n: number) => String(n).padStart(2, '0');
      return fmt
        .replace('YYYY', String(d.getFullYear()))
        .replace('MM', pad(d.getMonth() + 1))
        .replace('DD', pad(d.getDate()))
        .replace('HH', pad(d.getHours()))
        .replace('mm', pad(d.getMinutes()))
        .replace('ss', pad(d.getSeconds()));
    }
    case 'string': {
      const suffix = format || '';
      return `${v}${suffix}`;
    }
    case 'jsonpath':
      return resolvePath(v, format);
    default:
      return v;
  }
}

/** 字段映射：sourceField → targetField，支持格式化 */
export function applyMapping(raw: unknown, mapping?: DataFieldMapping[]): unknown {
  if (!mapping || mapping.length === 0 || !Array.isArray(raw)) return raw;
  return raw.map((item: any) => {
    if (!item || typeof item !== 'object') return item;
    const out: Record<string, unknown> = { ...item };
    for (const m of mapping) {
      // 支持 jsonpath 源字段
      let v = m.sourceField.includes('.') || m.sourceField.includes('[')
        ? resolvePath(item, m.sourceField)
        : item[m.sourceField];
      // 可视化解析：单位换算（先换算再格式化）
      if (v !== undefined && v !== null && m.unit && m.unit.kind !== 'none') {
        const n = Number(v);
        if (!Number.isNaN(n)) {
          if (m.unit.kind === 'cToF') v = n * 9 / 5 + 32;
          else if (m.unit.kind === 'fToC') v = (n - 32) * 5 / 9;
          else v = n * (m.unit.scale ?? 1) + (m.unit.offset ?? 0);
        }
      }
      if (v !== undefined && m.targetField) {
        out[m.targetField] = formatValue(v, m.type, m.format);
      }
    }
    return out;
  });
}

/** 数据过滤：表达式对每条 item 求值（上下文 item），返回 true 保留 */
function applyFilter(data: unknown, filter?: string): unknown {
  if (!filter || !Array.isArray(data)) return data;
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function('item', 'index', 'data', `return (${filter});`);
    return data.filter((item: any, index: number) => {
      try { return !!fn(item, index, data); } catch { return true; }
    });
  } catch {
    return data;
  }
}

/** JS 转换：对整个数据集执行，入参 data */
function applyTransformFn(data: unknown, transform?: string): unknown {
  if (!transform) return data;
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function('data', `return (${transform});`);
    const out = fn(data);
    return out ?? data;
  } catch {
    return data;
  }
}

/** 完整数据解析管线 */
export function parseData(raw: unknown, parser?: DataParserConfig): unknown {
  if (!parser) return raw;
  let data = resolvePath(raw, parser.path);
  data = applyMapping(data, parser.mapping);
  data = applyFilter(data, parser.filter);
  data = applyTransformFn(data, parser.transform);
  return data;
}

export async function fetchDataSource(ds: any, signal?: AbortSignal): Promise<unknown> {
  const src = normalize(ds);
  if (!src) return [];
  // 插件数据源类型（阶段3）：路由到插件注册的 fetch 实现
  if (src.type && !['static', 'api', 'websocket', 'sql', 'mqtt'].includes(src.type)) {
    const { dataSourceTypeRegistry } = await import('../plugins/data-source-type-registry');
    const pt = dataSourceTypeRegistry.get(src.type);
    if (pt?.fetch) return pt.fetch((src.config as any) || {});
    if (pt?.test) return pt.test((src.config as any) || {});
    throw new Error(`数据源类型 ${src.type} 未注册或插件未提供 fetch 实现`);
  }
  switch (src.type) {
    case 'static':
      return (src.config as any)?.data ?? [];
    case 'api': {
      const cfg = src.config as any;
      const method = (cfg.method || 'GET') as 'GET' | 'POST' | 'PUT' | 'DELETE';
      const headers = cfg.headers || {};
      const params = cfg.params || {};
      const body = cfg.body || (method === 'POST' ? params : undefined);
      const res = await client.request({
        url: cfg.url,
        method,
        params: method === 'GET' ? params : undefined,
        data: method === 'POST' || method === 'PUT' ? body : undefined,
        headers,
        signal,
        timeout: 15000,
      });
      return res.data;
    }
    case 'sql': {
      const res = await client.post('/proxy/query', { type: 'sql', config: src.config }, { signal, timeout: 20000 });
      return res.data;
    }
    case 'mqtt': {
      const res = await client.post('/proxy/query', { type: 'mqtt', config: src.config }, { signal, timeout: 20000 });
      return res.data;
    }
    case 'websocket': {
      // 原生 WebSocket 持续订阅：一次连接，收到消息即回调，保持监听
      const cfg = src.config as any;
      return new Promise<unknown>((resolve, reject) => {
        let ws: WebSocket | null = null;
        let timer: ReturnType<typeof setTimeout> | null = null;
        let done = false;
        const cleanup = () => {
          if (timer) { clearTimeout(timer); timer = null; }
          if (ws) { ws.onmessage = null; ws.onerror = null; ws.onclose = null; ws.close(); ws = null; }
          if (signal?.aborted && !done) { done = true; reject(new Error('aborted')); }
        };
        try {
          ws = new WebSocket(cfg.url, cfg.protocol || undefined);
        } catch (e: any) {
          reject(e);
          return;
        }
        ws.onopen = () => {
          if (timer) { clearTimeout(timer); timer = null; }
        };
        ws.onmessage = (ev) => {
          const raw = ev.data;
          let parsed: unknown = raw;
          if (typeof raw === 'string') {
            try { parsed = JSON.parse(raw); } catch { /* 保留原文 */ }
          }
          if (timer) { clearTimeout(timer); timer = null; }
          if (!done) {
            done = true;
            resolve(parsed);
          }
        };
        ws.onerror = () => { if (!done) { done = true; cleanup(); reject(new Error('WebSocket 连接错误')); } };
        ws.onclose = () => { if (!done) { done = true; cleanup(); } };
        if (signal) {
          signal.addEventListener('abort', cleanup, { once: true });
        }
        timer = setTimeout(() => { if (!done) { done = true; cleanup(); reject(new Error('WebSocket 连接超时')); } }, 8000);
      });
    }
    default:
      return [];
  }
}

/** 静态数据同步读取（供无 hook 场景回退） */
export function readStaticData(ds: any): unknown {
  const src = normalize(ds);
  if (src?.type === 'static') return (src.config as any)?.data ?? [];
  return [];
}

/**
 * WebSocket 共享连接池：同一 url 只建一条连接，多订阅者共享消息。
 * 所有订阅者清理后关闭连接；断线自动重连（指数退避，最多 5 次）。
 */
const wsPool = new Map<string, {
  ws: WebSocket | null;
  listeners: Set<(data: unknown) => void>;
  errorListeners: Set<(e: any) => void>;
  retries: number;
  retryTimer: ReturnType<typeof setTimeout> | null;
}>();

function getPoolKey(url: string, protocol?: string): string {
  return `${protocol || ''}::${url}`;
}

function connectPooled(key: string, url: string, protocol?: string) {
  const entry = wsPool.get(key);
  if (!entry) return;
  if (entry.ws && (entry.ws.readyState === WebSocket.OPEN || entry.ws.readyState === WebSocket.CONNECTING)) return;
  try {
    const ws = new WebSocket(url, protocol || undefined);
    entry.ws = ws;
    ws.onopen = () => { entry.retries = 0; };
    ws.onmessage = (ev) => {
      const raw = ev.data;
      let parsed: unknown = raw;
      if (typeof raw === 'string') {
        try { parsed = JSON.parse(raw); } catch { /* 保留原文 */ }
      }
      entry.listeners.forEach((fn) => fn(parsed));
    };
    ws.onerror = () => {
      entry.errorListeners.forEach((fn) => fn(new Error('WebSocket 连接错误')));
    };
    ws.onclose = () => {
      entry.ws = null;
      // 有订阅者时自动重连（指数退避）
      if (entry.listeners.size > 0) {
        if (entry.retries < 5) {
          entry.retries++;
          entry.retryTimer = setTimeout(() => connectPooled(key, url, protocol), 1000 * entry.retries);
        } else {
          entry.errorListeners.forEach((fn) => fn(new Error('WebSocket 重连失败（已尝试 5 次）')));
        }
      }
    };
  } catch (e: any) {
    entry.errorListeners.forEach((fn) => fn(e));
  }
}

/**
 * 流式数据源订阅：WebSocket 持续推送（共享连接池），每条消息都回调 onMessage。
 * 返回清理函数。断开/异常也会回调 onError。
 */
export function subscribeDataSource(
  ds: any,
  opts: { onMessage: (data: unknown) => void; onError?: (e: any) => void },
): () => void {
  const src = normalize(ds);
  if (!src) return () => {};
  if (src.type !== 'websocket') {
    opts.onError?.(new Error('仅 WebSocket 数据源支持流式订阅'));
    return () => {};
  }
  const cfg = src.config as any;
  const key = getPoolKey(cfg.url, cfg.protocol);
  let entry = wsPool.get(key);
  if (!entry) {
    entry = { ws: null, listeners: new Set(), errorListeners: new Set(), retries: 0, retryTimer: null };
    wsPool.set(key, entry);
  }
  const listener = (data: unknown) => opts.onMessage(data);
  const errListener = (e: any) => opts.onError?.(e);
  entry.listeners.add(listener);
  if (opts.onError) entry.errorListeners.add(errListener);
  connectPooled(key, cfg.url, cfg.protocol);

  return () => {
    const cur = wsPool.get(key);
    if (!cur) return;
    cur.listeners.delete(listener);
    if (opts.onError) cur.errorListeners.delete(errListener);
    // 无订阅者时关闭连接并清理
    if (cur.listeners.size === 0) {
      if (cur.retryTimer) { clearTimeout(cur.retryTimer); cur.retryTimer = null; }
      if (cur.ws) {
        cur.ws.onopen = null; cur.ws.onmessage = null; cur.ws.onerror = null; cur.ws.onclose = null;
        try { cur.ws.close(); } catch { /* ignore */ }
        cur.ws = null;
      }
      wsPool.delete(key);
    }
  };
}
